/**
 * UpgradeOfferState (Rob, 2026-10-14).
 *
 * WHY THIS EXISTS:
 * Every upgrade prompt (plan gate, paywall popup, usage, subscription) must
 * show the next plan, why, what you get and the price, all from Node's
 * upgradeOffer. Before this each surface handled a missing offer its own way,
 * usually with a "View Plans" button and no price. This wraps the shared
 * UpgradeOfferSummary with the two other honest states:
 * - loading: a shimmer skeleton, never a guessed price;
 * - unavailable: says so, with the upgrade button disabled.
 */
import React from 'react';
import { Button, Skeleton } from '../../design-system';
import UpgradeOfferSummary, { type UpgradeOfferPresentation } from './UpgradeOfferSummary';
import './UpgradeOfferSummary.css';

type SummaryProps = Omit<React.ComponentProps<typeof UpgradeOfferSummary>, 'offer'>;

export type UpgradeOfferStateProps = SummaryProps & {
  offer: UpgradeOfferPresentation | null | undefined;
  loading: boolean;
  error?: string | null;
};

export default function UpgradeOfferState({
  offer,
  loading,
  error,
  compact = false,
  reason,
  secondaryAction,
  ...summaryProps
}: UpgradeOfferStateProps): React.ReactElement {
  const className = `upgrade-offer-summary${compact ? ' upgrade-offer-summary--compact' : ''}`;
  if (offer) {
    return (
      <UpgradeOfferSummary
        offer={offer}
        compact={compact}
        reason={reason}
        secondaryAction={secondaryAction}
        {...summaryProps}
      />
    );
  }
  if (loading) {
    return (
      <section
        className={className}
        aria-label="Loading plan upgrade"
        aria-busy="true"
        data-testid="upgrade-offer-loading"
      >
        <Skeleton height={14} width="40%" />
        <Skeleton height={24} width="70%" />
        <Skeleton height={14} width="90%" />
        <Skeleton height={14} width="60%" />
        <div className="upgrade-offer-summary__footer">
          <Skeleton height={28} width={120} />
          {summaryProps.onUpgrade ? <Skeleton height={44} width={180} /> : null}
        </div>
      </section>
    );
  }
  return (
    <section
      className={className}
      aria-label="Plan upgrade"
      role="status"
      data-testid="upgrade-offer-unavailable"
    >
      <header className="upgrade-offer-summary__header">
        <h3 className="upgrade-offer-summary__title">Upgrade price unavailable</h3>
        <p className="upgrade-offer-summary__reason">
          {reason ? `${reason} ` : ''}We could not load your upgrade price. Refresh the page to try
          again.
        </p>
      </header>
      <footer className="upgrade-offer-summary__footer">
        {/* Only surfaces that own their button get one; chat cards keep theirs. */}
        {summaryProps.onUpgrade ? (
          <Button className="upgrade-offer-summary__action" disabled>
            Upgrade
          </Button>
        ) : null}
        <p className="upgrade-offer-summary__note">
          {error ? 'Plan options did not load.' : 'No plan offer was returned.'}
        </p>
      </footer>
      {secondaryAction ? (
        <div className="upgrade-offer-summary__secondary">{secondaryAction}</div>
      ) : null}
    </section>
  );
}
